import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCurrency, formatPercentage, formatNumber } from '@/lib/utils/format';
import type { ModelEffectivenessEntry } from '../types/analytics.types';

interface ModelsTabProps {
  data: ModelEffectivenessEntry[] | null;
  isLoading: boolean;
}

export function ModelsTab({ data, isLoading }: ModelsTabProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Efectividad de Modelos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-5 w-20" />
                <Skeleton className="h-5 w-20" />
                <Skeleton className="h-5 w-24" />
                <Skeleton className="h-5 w-20 ml-auto" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Efectividad de Modelos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-32 flex items-center justify-center text-muted-foreground text-sm">
            Sin datos de modelos para este periodo
          </div>
        </CardContent>
      </Card>
    );
  }

  const sorted = [...data].sort((a, b) => b.totalRevenue - a.totalRevenue);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Efectividad de Modelos de Pricing</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Modelo</TableHead>
                <TableHead className="text-right">Margen Objetivo</TableHead>
                <TableHead className="text-right">Margen Real</TableHead>
                <TableHead className="text-right">Cotizaciones</TableHead>
                <TableHead className="text-right">Ingresos</TableHead>
                <TableHead className="text-right">Win Rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((model) => {
                const marginMet = model.avgMargin >= model.targetMargin;
                return (
                  <TableRow key={model.modelId}>
                    <TableCell className="font-medium">{model.modelName}</TableCell>
                    <TableCell className="text-right">
                      {formatPercentage(model.targetMargin)}
                    </TableCell>
                    <TableCell className="text-right">
                      <span
                        className={
                          marginMet ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'
                        }
                      >
                        {formatPercentage(model.avgMargin)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {formatNumber(model.totalQuotes, 0)}
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatCurrency(model.totalRevenue)}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={model.winRate >= 50 ? 'text-green-600' : 'text-amber-600'}>
                        {formatPercentage(model.winRate)}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
